import { MarkerType, Position, type Edge, type Node } from 'reactflow';
import type { SimulationGraph, SimulationNode, SimulationResultPayload } from '../../models/simulation';
import { deriveFormulaUnit } from '../../utils/formulaUnits';
import { getAllAncestors, getImmediateAncestors, getImmediateDescendants } from '../../utils/graph';
import { formatReadableNumber } from '../../utils/numberFormatting';
import { nodeClassMap } from './nodeStyles';

export type GraphNodeDisplayData = SimulationNode['data'] & {
  displayUnit?: string;
  metricLabel: string;
  metricValue: string;
  metricUnit?: string;
  isHighlighted: boolean;
  onOpenOptions: (nodeId: string) => void;
};

interface BuildGraphViewModelInput {
  graph: SimulationGraph;
  selectedNodeId?: string;
  hoveredOutputNodeId: string | null;
  result?: SimulationResultPayload;
  onOpenOptions: (nodeId: string) => void;
}

const getDisplayUnit = (node: SimulationNode, nodes: SimulationNode[]): string | undefined => {
  if (node.data.type === 'distribution') {
    return node.data.unit;
  }

  return deriveFormulaUnit(node, nodes);
};

const getMetricDisplay = (
  node: SimulationNode,
  nodes: SimulationNode[],
  result?: SimulationResultPayload
): { metricLabel: string; metricValue: string; metricUnit?: string } => {
  const displayUnit = getDisplayUnit(node, nodes);
  const nodeResult = result?.results[node.id];
  const statistic = node.data.statistic ?? 'mean';

  if (nodeResult?.summary) {
    const value = statistic === 'median'
      ? nodeResult.summary.median
      : statistic === 'p10'
        ? nodeResult.summary.p10
        : statistic === 'p90'
          ? nodeResult.summary.p90
          : statistic === 'std'
            ? nodeResult.summary.std
            : nodeResult.summary.mean;

    if (Number.isFinite(value)) {
      return {
        metricLabel: statistic === 'std' ? 'Std dev' : statistic.toUpperCase(),
        metricValue: formatReadableNumber(value),
        metricUnit: nodeResult.unit ?? displayUnit
      };
    }
  }

  if (node.data.type === 'distribution' && node.data.config.kind === 'fixed') {
    return {
      metricLabel: 'Fixed value',
      metricValue: formatReadableNumber(node.data.config.value),
      metricUnit: displayUnit
    };
  }

  return {
    metricLabel: node.data.type === 'distribution' ? 'Distribution' : 'Formula',
    metricValue: node.data.type === 'distribution' ? node.data.config.kind : 'Ready',
    metricUnit: displayUnit
  };
};

export const buildGraphViewModel = ({
  graph,
  selectedNodeId,
  hoveredOutputNodeId,
  result,
  onOpenOptions
}: BuildGraphViewModelInput): { nodes: Node<GraphNodeDisplayData>[]; edges: Edge[] } => {
  const hoverHighlightedNodes = hoveredOutputNodeId
    ? getAllAncestors(hoveredOutputNodeId, graph.edges)
    : new Set<string>();
  if (hoveredOutputNodeId) {
    hoverHighlightedNodes.add(hoveredOutputNodeId);
  }

  const highlightedNodes = selectedNodeId
    ? getImmediateAncestors(selectedNodeId, graph.edges)
    : new Set<string>();
  const dependentNodes = selectedNodeId
    ? getImmediateDescendants(selectedNodeId, graph.edges)
    : new Set<string>();

  const highlightedEdges = new Set<string>();
  if (selectedNodeId) {
    graph.edges.forEach((edge) => {
      if (
        (highlightedNodes.has(edge.source) && highlightedNodes.has(edge.target)) ||
        (highlightedNodes.has(edge.source) && edge.target === selectedNodeId) ||
        (edge.source === selectedNodeId && dependentNodes.has(edge.target)) ||
        (dependentNodes.has(edge.source) && dependentNodes.has(edge.target))
      ) {
        highlightedEdges.add(edge.id);
      }
    });
  }

  const nodes = graph.nodes.map((node) => {
    const isDependencyHighlighted = highlightedNodes.has(node.id);
    const isDependentHighlighted = dependentNodes.has(node.id);
    const isHoverHighlighted = hoverHighlightedNodes.size > 0 && hoverHighlightedNodes.has(node.id);
    const isSelected = node.id === selectedNodeId;
    const isSelectionRelated = isDependencyHighlighted || isDependentHighlighted || isSelected;
    const isDimmed =
      hoverHighlightedNodes.size > 0
        ? !isHoverHighlighted
        : Boolean(selectedNodeId) && !isSelectionRelated;
    const nodeClassType = node.data.isOutput ? 'output' : node.data.type;
    const displayUnit = getDisplayUnit(node, graph.nodes);
    const metricDisplay = getMetricDisplay(node, graph.nodes, result);

    const className = [
      'simulation-node-wrapper',
      nodeClassMap[nodeClassType],
      isDependencyHighlighted ? 'node-highlighted' : '',
      isDependentHighlighted ? 'node-dependent-highlighted' : '',
      isSelected ? 'node-selected-highlighted' : '',
      isHoverHighlighted ? 'node-hover-highlighted' : '',
      isDimmed ? 'node-dimmed' : ''
    ].filter(Boolean).join(' ');

    return {
      id: node.id,
      position: node.position,
      draggable: false,
      type: 'simulation',
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      data: {
        ...node.data,
        displayUnit,
        ...metricDisplay,
        isHighlighted: isDependencyHighlighted || isDependentHighlighted || isSelected,
        onOpenOptions
      },
      className
    };
  });

  const edges = graph.edges.map((edge) => ({
    ...edge,
    type: 'default',
    className: highlightedEdges.has(edge.id) ? 'edge-highlighted' : '',
    animated: false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 10,
      height: 10,
      markerUnits: 'userSpaceOnUse' as const
    },
    style: {
      strokeWidth: highlightedEdges.has(edge.id) ? 2.4 : 1.7
    }
  }));

  return { nodes, edges };
};
